/** Back office → Academy marketing (D-111): ready-made Meta campaigns (sandbox), generated visuals,
 * public live demonstrations, the free mock exam (D-172) and the UTM → lead → plan attribution. */

import {
  type AcademyTemplate,
  type AdCreativeFormatEnum,
  type Webinar,
  useAdsCampaignsAcademyTemplatesList,
  useAdsCampaignsAttributionRetrieve,
  useAdsCampaignsFromAcademyTemplateCreate,
  useAdsCreativesCreate,
  useAdsCreativesList,
  type FreeMockOverview,
  useMarketingSettingsFreeMockPartialUpdate,
  useMarketingSettingsFreeMockRetrieve,
  useMarketingWebinarsCreate,
  useMarketingWebinarsList,
  useMarketingWebinarsRegistrationsList,
} from "@agencyos/api-client";
import { Badge, Button, EmptyState, GlassCard, GlassSheet, SelectField, StatCard, Tabs, TextField, tabPanelProps } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, Megaphone, Plus, Radio, Target } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Page } from "../../components/AppShell";
import { fieldErrors } from "../../lib/settings";
import { ModeNotice, useAdsLive } from "../ads/shared";
import "./academyMarketing.css";

type Tab = "campaigns" | "webinars" | "freeMock" | "attribution";
const TABS: Tab[] = ["campaigns", "webinars", "freeMock", "attribution"];
const FORMATS: AdCreativeFormatEnum[] = ["square", "story", "landscape"];
const EXAMS = ["tcf_canada", "tef_canada", "ielts_general"] as const;

function useRefresh(prefix: string) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith(prefix) });
}

function text(value: unknown, language: string): string {
  if (value && typeof value === "object") {
    const map = value as Record<string, string>;
    return map[language] ?? map.fr ?? map.en ?? "";
  }
  return typeof value === "string" ? value : "";
}

function TemplateCard({ row }: { row: AcademyTemplate }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const create = useAdsCampaignsFromAcademyTemplateCreate();
  const visual = useAdsCreativesCreate();
  const creatives = useAdsCreativesList({ template: row.key });
  const refresh = useRefresh("/api/v1/ads/");
  const [format, setFormat] = useState<AdCreativeFormatEnum>("square");
  const language = row.languages.includes(i18n.language) ? i18n.language : (row.languages[0] ?? "fr");
  const images = (creatives.data?.status === 200 ? creatives.data.data.results : []).filter((c) => c.status === "ready" && c.image_url);
  return (
    <GlassCard as="article" className="stack am-template" aria-labelledby={`tpl-${row.key}`}>
      <div className="row between">
        <h3 id={`tpl-${row.key}`}>{row.name}</h3>
        <Badge tone={row.simulated_only ? "offer" : "default"}>{t(`academyMarketing.objective.${row.objective}`)}</Badge>
      </div>
      <p className="muted">
        {t("academyMarketing.campaigns.countries")} : {row.countries.join(", ")} · {t("academyMarketing.campaigns.languages")} : {row.languages.join(", ").toUpperCase()}
      </p>
      {row.simulated_only ? <p className="muted">{t("academyMarketing.campaigns.simulatedOnly")}</p> : null}
      {row.error ? (
        <p role="alert" className="ko">
          {row.error}
        </p>
      ) : (
        <ul className="am-copies" aria-label={t("academyMarketing.campaigns.copies")}>
          {row.copies.map((c, i) => (
            <li key={`${c.language}-${i}`} lang={c.language}>
              <strong>{c.headline}</strong>
              <span>{c.text}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="row wrap">
        <Button
          icon={<Plus size={16} aria-hidden="true" />}
          disabled={Boolean(row.error)}
          loading={create.isPending}
          onClick={() =>
            create.mutate(
              { data: { template: row.key, language: language === "en" ? "en" : "fr" } },
              {
                onSuccess: (r) => {
                  if (r.status === 201) {
                    refresh();
                    navigate(`/ads/${r.data.id}`);
                  }
                },
              },
            )
          }
        >
          {t("academyMarketing.campaigns.create")}
        </Button>
        <SelectField
          label={t("academyMarketing.campaigns.format")}
          value={format}
          options={FORMATS.map((f) => ({ value: f, label: t(`academyMarketing.format.${f}`) }))}
          onChange={(e) => setFormat(e.target.value as AdCreativeFormatEnum)}
        />
        <Button
          variant="secondary"
          disabled={Boolean(row.error)}
          loading={visual.isPending}
          onClick={() =>
            visual.mutate(
              { data: { template_key: row.key, format, language: language === "en" ? "en" : "fr", theme: "midnight" } },
              { onSuccess: () => refresh() },
            )
          }
        >
          {t("academyMarketing.campaigns.visual")}
        </Button>
      </div>
      {images.length ? (
        <div className="am-visuals">
          {images.slice(0, 3).map((c) => (
            <a key={c.id} href={c.image_url ?? "#"} target="_blank" rel="noopener noreferrer">
              <img src={c.image_url ?? ""} alt={t("academyMarketing.campaigns.visualAlt", { format: t(`academyMarketing.format.${c.format}`), headline: c.headline })} loading="lazy" />
            </a>
          ))}
        </div>
      ) : null}
    </GlassCard>
  );
}

function CampaignsTab() {
  const { t, i18n } = useTranslation();
  const live = useAdsLive();
  const templates = useAdsCampaignsAcademyTemplatesList({ language: i18n.language === "en" ? "en" : "fr" });
  const rows = templates.data?.status === 200 ? templates.data.data : [];
  return (
    <div className="stack">
      <ModeNotice live={live} />
      <p className="muted">{t("academyMarketing.campaigns.intro")}</p>
      <div className="am-grid">
        {rows.map((row) => (
          <TemplateCard key={row.key} row={row} />
        ))}
      </div>
    </div>
  );
}

function Registrations({ webinar }: { webinar: Webinar }) {
  const { t } = useTranslation();
  const list = useMarketingWebinarsRegistrationsList(webinar.id);
  const rows = list.data?.status === 200 ? list.data.data : [];
  if (!rows.length) return <p className="muted">{t("academyMarketing.webinars.none")}</p>;
  return (
    <div className="table-wrap">
      <table className="table" aria-label={t("academyMarketing.webinars.registrations")}>
        <thead>
          <tr>
            <th scope="col">{t("academyMarketing.webinars.name")}</th>
            <th scope="col">{t("academyMarketing.webinars.email")}</th>
            <th scope="col">{t("academyMarketing.webinars.country")}</th>
            <th scope="col">{t("academyMarketing.webinars.source")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{`${r.first_name} ${r.last_name}`.trim()}</td>
              <td>{r.email}</td>
              <td>{r.country}</td>
              <td>{String((r.tracking as Record<string, string> | undefined)?.utm_campaign ?? "—")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewWebinarSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useMarketingWebinarsCreate();
  const refresh = useRefresh("/api/v1/marketing/");
  const [titleFr, setTitleFr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [summary, setSummary] = useState("");
  const [start, setStart] = useState("");
  const [language, setLanguage] = useState("fr");
  const [capacity, setCapacity] = useState("100");
  const errors = fieldErrors(create.error);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        data: {
          title: { fr: titleFr, en: titleEn || titleFr },
          summary: { fr: summary },
          exams: [...EXAMS],
          language,
          starts_at: new Date(start).toISOString(),
          duration_minutes: 45,
          capacity: Number(capacity) || 100,
        },
      },
      {
        onSuccess: (r) => {
          if (r.status === 201) {
            refresh();
            onClose();
          }
        },
      },
    );
  };
  return (
    <GlassSheet open={open} title={t("academyMarketing.webinars.new")} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <TextField label={t("academyMarketing.webinars.titleFr")} value={titleFr} error={errors.title} onChange={(e) => setTitleFr(e.target.value)} />
        <TextField label={t("academyMarketing.webinars.titleEn")} value={titleEn} onChange={(e) => setTitleEn(e.target.value)} />
        <TextField label={t("academyMarketing.webinars.summary")} value={summary} onChange={(e) => setSummary(e.target.value)} />
        <div className="grid-2">
          <TextField label={t("academyMarketing.webinars.start")} type="datetime-local" value={start} error={errors.starts_at} onChange={(e) => setStart(e.target.value)} />
          <TextField label={t("academyMarketing.webinars.capacity")} inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        </div>
        <SelectField
          label={t("academyMarketing.webinars.language")}
          value={language}
          options={["fr", "en", "fr-en"].map((v) => ({ value: v, label: t(`academyMarketing.webinars.lang.${v}`) }))}
          onChange={(e) => setLanguage(e.target.value)}
        />
        <p className="muted">{t("academyMarketing.webinars.jitsi")}</p>
        <div className="row">
          <Button type="submit" loading={create.isPending} disabled={!titleFr || !start}>
            {t("academyMarketing.webinars.create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

function WebinarsTab() {
  const { t, i18n } = useTranslation();
  const list = useMarketingWebinarsList();
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string>("");
  return (
    <div className="stack">
      <div className="row between">
        <p className="muted">{t("academyMarketing.webinars.intro")}</p>
        <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
          {t("academyMarketing.webinars.new")}
        </Button>
      </div>
      {rows.length ? (
        rows.map((w) => (
          <GlassCard key={w.id} as="article" className="stack" aria-labelledby={`w-${w.id}`}>
            <div className="row between">
              <h3 id={`w-${w.id}`}>{text(w.title, i18n.language)}</h3>
              <Badge tone="default">{t("academyMarketing.webinars.registered", { count: w.registered })}</Badge>
            </div>
            <p className="muted">
              {new Intl.DateTimeFormat(i18n.language, { dateStyle: "full", timeStyle: "short", timeZone: "Africa/Douala" }).format(new Date(w.starts_at))} ·{" "}
              {w.join_url ? (
                <a className="link" href={w.join_url} target="_blank" rel="noopener noreferrer">
                  {t("academyMarketing.webinars.join")}
                </a>
              ) : (
                t("academyMarketing.webinars.linkPending")
              )}{" "}
              ·{" "}
              <a className="link" href={w.public_url} target="_blank" rel="noopener noreferrer">
                {t("academyMarketing.webinars.publicPage")}
              </a>
            </p>
            <Button variant="ghost" aria-expanded={open === w.id} onClick={() => setOpen(open === w.id ? "" : w.id)}>
              {t("academyMarketing.webinars.registrations")}
            </Button>
            {open === w.id ? <Registrations webinar={w} /> : null}
          </GlassCard>
        ))
      ) : (
        <EmptyState icon={<Radio size={24} aria-hidden="true" />} title={t("academyMarketing.webinars.empty")} description={t("academyMarketing.webinars.emptyText")} />
      )}
      <NewWebinarSheet open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

/** D-172: the settings of the free mock (data of the Academy settings). */
const LIMITS = ["per_account", "ai_per_agency_day", "speaking_ai_per_agency_day"] as const;
type LimitKey = (typeof LIMITS)[number];

function LimitsForm({ row }: { row: FreeMockOverview }) {
  const { t } = useTranslation();
  const update = useMarketingSettingsFreeMockPartialUpdate();
  const refresh = useRefresh("/api/v1/marketing/");
  const [values, setValues] = useState<Record<LimitKey, string>>(() => Object.fromEntries(LIMITS.map((k) => [k, String(row[k])])) as Record<LimitKey, string>);
  const [saved, setSaved] = useState(false);
  const errors = fieldErrors(update.error);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    const data = Object.fromEntries(LIMITS.filter((k) => values[k].trim() !== "").map((k) => [k, Number(values[k])]));
    update.mutate({ data }, { onSuccess: () => { setSaved(true); refresh(); } });
  };
  return (
    <form className="stack" onSubmit={submit} aria-labelledby="am-limits">
      <h3 id="am-limits">{t("academyMarketing.freeMock.limits")}</h3>
      <p className="muted">{t("academyMarketing.freeMock.limitsIntro")}</p>
      <div className="am-limits">
        {LIMITS.map((key) => (
          <TextField
            key={key}
            type="number"
            inputMode="numeric"
            min={0}
            max={key === "per_account" ? 5 : undefined}
            label={t(`academyMarketing.freeMock.${key}`)}
            value={values[key]}
            error={errors[key]}
            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
          />
        ))}
      </div>
      <div className="row wrap">
        <Button type="submit" loading={update.isPending}>
          {t("academyMarketing.freeMock.save")}
        </Button>
        {saved ? (
          <span role="status" className="muted">
            {t("academyMarketing.freeMock.saved")}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function FreeMockTab() {
  const { t } = useTranslation();
  const overview = useMarketingSettingsFreeMockRetrieve();
  const update = useMarketingSettingsFreeMockPartialUpdate();
  const refresh = useRefresh("/api/v1/marketing/");
  const row = overview.data?.status === 200 ? overview.data.data : undefined;
  return (
    <div className="stack">
      <p className="muted">{t("academyMarketing.freeMock.intro")}</p>
      {row?.warning ? (
        <p className="ko" role="alert">
          {t("academyMarketing.freeMock.warning")}
        </p>
      ) : null}
      {row ? (
        <>
          <div className="am-stats">
            <StatCard label={t("academyMarketing.freeMock.started", { days: row.days })} value={row.started} />
            <StatCard label={t("academyMarketing.freeMock.finished")} value={row.finished} />
            <StatCard label={t("academyMarketing.freeMock.delayed")} value={row.delayed} />
          </div>
          <GlassCard as="section" className="stack" aria-label={t("academyMarketing.freeMock.limits")}>
            <div className="row between">
              <strong>{row.enabled ? t("academyMarketing.freeMock.on") : t("academyMarketing.freeMock.off")}</strong>
              <Button variant="secondary" loading={update.isPending} onClick={() => update.mutate({ data: { enabled: !row.enabled } }, { onSuccess: () => refresh() })}>
                {row.enabled ? t("academyMarketing.freeMock.disable") : t("academyMarketing.freeMock.enable")}
              </Button>
            </div>
            <p className="muted">{t("academyMarketing.freeMock.mocksIntro")}</p>
            <div className="table-wrap">
              <table className="table" aria-label={t("academyMarketing.freeMock.mocks")}>
                <thead>
                  <tr>
                    <th scope="col">{t("academyMarketing.freeMock.exam")}</th>
                    <th scope="col">{t("academyMarketing.freeMock.mock")}</th>
                    <th scope="col">{t("academyMarketing.freeMock.minutes")}</th>
                    <th scope="col">{t("academyMarketing.freeMock.state")}</th>
                  </tr>
                </thead>
                <tbody>
                  {row.mocks.map((m) => (
                    <tr key={`${m.exam}-${m.level}`}>
                      <td>
                        {t(`academyMarketing.exam.${m.exam}`)}
                        {m.level ? ` · ${m.level}` : ""}
                      </td>
                      <td className="ao-num">{m.title ? `${m.mock} · ${m.title}` : m.mock}</td>
                      <td className="ao-num">{m.minutes || "—"}</td>
                      <td>
                        <Badge tone={m.ready ? "success" : "warning"}>{m.ready ? t("academyMarketing.freeMock.ready") : t("academyMarketing.freeMock.notReady")}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <LimitsForm key={`${row.per_account}-${row.ai_per_agency_day}-${row.speaking_ai_per_agency_day}`} row={row} />
          </GlassCard>
        </>
      ) : null}
    </div>
  );
}

function AttributionTab() {
  const { t, i18n } = useTranslation();
  const report = useAdsCampaignsAttributionRetrieve({ days: 90 });
  const data = report.data?.status === 200 ? report.data.data : undefined;
  const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(v)} %`);
  return (
    <div className="stack">
      <p className="muted">{t("academyMarketing.attribution.intro")}</p>
      {data ? (
        <>
          <div className="am-stats">
            <StatCard label={t("academyMarketing.attribution.leads")} value={data.totals.leads} />
            <StatCard label={t("academyMarketing.attribution.accounts")} value={data.totals.accounts} />
            <StatCard label={t("academyMarketing.attribution.subscribers")} value={data.totals.subscribers} />
          </div>
          <div className="table-wrap">
            <table className="table" aria-label={t("academyMarketing.attribution.table")}>
              <thead>
                <tr>
                  <th scope="col">{t("academyMarketing.attribution.campaign")}</th>
                  <th scope="col">{t("academyMarketing.attribution.source")}</th>
                  <th scope="col">{t("academyMarketing.attribution.leads")}</th>
                  <th scope="col">{t("academyMarketing.attribution.accounts")}</th>
                  <th scope="col">{t("academyMarketing.attribution.subscribers")}</th>
                  <th scope="col">{t("academyMarketing.attribution.rate")}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={`${r.campaign}-${r.source}-${r.medium}`}>
                    <td>{r.campaign}</td>
                    <td>{[r.source, r.medium].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="ao-num">{r.leads}</td>
                    <td className="ao-num">{r.accounts}</td>
                    <td className="ao-num">{r.subscribers}</td>
                    <td className="ao-num">{pct(r.subscriber_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function AcademyMarketingPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("campaigns");
  const icons = { campaigns: Megaphone, webinars: Radio, freeMock: ClipboardCheck, attribution: Target };
  return (
    <Page title={t("academyMarketing.title")} description={t("academyMarketing.intro")}>
      <Tabs
        idPrefix="am"
        label={t("academyMarketing.title")}
        value={tab}
        onChange={setTab}
        items={TABS.map((value) => {
          const Icon = icons[value];
          return { value, label: t(`academyMarketing.tabs.${value}`), icon: <Icon size={16} aria-hidden="true" /> };
        })}
      />
      <div {...tabPanelProps("am", tab)} className="stack">
        {tab === "campaigns" ? <CampaignsTab /> : null}
        {tab === "webinars" ? <WebinarsTab /> : null}
        {tab === "freeMock" ? <FreeMockTab /> : null}
        {tab === "attribution" ? <AttributionTab /> : null}
      </div>
    </Page>
  );
}
